import { useState } from "react";
import { Copy, Check, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { publicInfoItems, hasPublicInfo, radrootConfig } from "@/config/radrootConfig";
import { Reveal, SectionHead } from "./primitives";

function CopyButton({ value, tid }) {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setDone(true);
      toast.success("Copied to clipboard");
      setTimeout(() => setDone(false), 1600);
    } catch {
      toast.error("Copy failed — select the value manually");
    }
  };
  return (
    <button type="button" className="copy-btn" data-testid={tid} onClick={copy} aria-label="Copy to clipboard">
      {done ? <Check size={15} /> : <Copy size={15} />}
    </button>
  );
}

function Item({ item }) {
  if (item.kind === "copy") {
    return (
      <div className="pub-value">
        <code>{item.value}</code>
        <CopyButton value={item.value} tid={`copy-${item.key}-btn`} />
      </div>
    );
  }
  if (item.kind === "link") {
    return (
      <div className="pub-value">
        <a href={item.value} target="_blank" rel="noopener noreferrer" data-testid={`link-${item.key}`}>
          {item.linkLabel || item.value}
          <ArrowUpRight size={14} />
        </a>
      </div>
    );
  }
  if (item.kind === "list-copy") {
    return (
      <div style={{ display: "grid", gap: 10 }}>
        {item.value.map((v) => (
          <div className="pub-value" key={v}>
            <code>{v}</code>
            <CopyButton value={v} tid={`copy-${item.key}-${v.slice(-6)}-btn`} />
          </div>
        ))}
      </div>
    );
  }
  if (item.kind === "email") {
    return (
      <div className="pub-value">
        <a href={`mailto:${item.value}`} data-testid={`email-${item.key}`}>
          {item.value}
        </a>
        <CopyButton value={item.value} tid={`copy-${item.key}-btn`} />
      </div>
    );
  }
  if (item.kind === "links") {
    return (
      <div className="pub-value" style={{ gap: 22, flexWrap: "wrap" }}>
        {item.value.map((s) => (
          <a key={s.url} href={s.url} target="_blank" rel="noopener noreferrer" data-testid={`social-${s.label || s.url}`}>
            {s.label || s.url}
            <ArrowUpRight size={14} />
          </a>
        ))}
      </div>
    );
  }
  return (
    <div className="pub-value">
      <code>{item.value}</code>
    </div>
  );
}

function AddrCard({ a }) {
  const tid = a.tag.toLowerCase();
  return (
    <div className="addr-card" data-testid={`addr-card-${tid}`}>
      <div className="addr-card-label">
        {a.label}
        <span className="addr-tag">{a.tag}</span>
      </div>
      <div className="addr-card-value">{a.value}</div>
      <div className="addr-card-foot">
        {a.balance ? (
          <span className="addr-balance">
            <i aria-hidden="true" />
            {a.balance}
          </span>
        ) : (
          <span />
        )}
        {a.full ? <CopyButton value={a.full} tid={`copy-${tid}-btn`} /> : null}
      </div>
    </div>
  );
}

function FspView({ data }) {
  return (
    <div>
      <div className="addr-grid">
        {data.addresses.map((a) => (
          <AddrCard key={a.tag} a={a} />
        ))}
      </div>
      <div className="prov-cols">
        <div className="prov-card">
          <div className="prov-card-title" data-testid="fast-updates-title">
            Fast updates addresses
          </div>
          {data.fastUpdates.map((f, i) => (
            <div className="fu-row" key={i} data-testid={`fast-update-${i + 1}`}>
              <div className="fu-left">
                <span className="fu-addr">{f.address}</span>
                <span className="fu-value">
                  <i aria-hidden="true" />
                  {f.value}
                </span>
              </div>
              <span className="fu-time">{f.timestamp}</span>
            </div>
          ))}
        </div>
        <div className="prov-card">
          <div className="prov-card-title">Nodes</div>
          {data.nodes.map((n) => (
            <div className="node-row" key={n} data-testid="node-id">
              {n}
            </div>
          ))}
        </div>
      </div>
      <div className="prov-card" style={{ marginTop: 18 }}>
        <div className="prov-card-title" data-testid="re-registration-title">
          RE Registration
        </div>
        {data.reRegistration.map((r) => (
          <div className="re-row" key={r.epoch} data-testid={`re-reg-${r.epoch.toLowerCase().replace("#", "")}`}>
            <span className="re-epoch">{r.epoch}</span>
            <span className="pill pill-live">
              <i aria-hidden="true" />
              {r.status}
            </span>
          </div>
        ))}
      </div>
      <div className="prov-card" style={{ marginTop: 18 }}>
        <div className="prov-card-title">Uptime Vote Signed</div>
        {data.uptimeVoteSigned.map((r) => (
          <div className="re-row" key={r.epoch} data-testid={`uptime-vote-${r.epoch.toLowerCase().replace("#", "")}`}>
            <span className="re-epoch">{r.epoch}</span>
            <span className="re-hash">{r.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function FtsoView({ data }) {
  return (
    <div>
      <div className="prov-cols" style={{ marginTop: 24 }}>
        <div className="prov-card">
          <div className="prov-card-title" data-testid="availability-title">
            Availability
          </div>
          {data.availability.map((a) => (
            <div className="ftso-stat" key={a.label}>
              <span className="ftso-stat-label">{a.label}</span>
              <b>{a.value}</b>
            </div>
          ))}
        </div>
        <div className="prov-card">
          <div className="prov-card-title" data-testid="success-rate-title">
            Success Rate
          </div>
          {data.successRate.map((s) => (
            <div className="ftso-stat" key={s.label}>
              <span className="ftso-stat-label">{s.label}</span>
              <span className="sr-pills">
                <span className="pill-mini">{s.primary}</span>
                <span className="pill-mini-o">{s.secondary}</span>
              </span>
            </div>
          ))}
        </div>
      </div>
      <div className="prov-card" style={{ marginTop: 18 }}>
        <div className="prov-card-title">RE</div>
        <div className="re-table" data-testid="ftso-epochs-table">
          <div className="re-thead">
            <span>RE</span>
            <span>Availability</span>
            <span>Success Rate</span>
          </div>
          {data.epochs.map((e) => (
            <div className="re-trow" key={e.epoch} data-testid={`ftso-${e.epoch.toLowerCase().replace("#", "")}`}>
              <span className="re-epoch">{e.epoch}</span>
              <span>{e.availability}</span>
              <span className="sr-pills">
                <span className="pill-mini">{e.primary}</span>
                <span className="pill-mini-o">{e.secondary}</span>
              </span>
            </div>
          ))}
        </div>
        <div className="sr-legend">
          <span>
            <i className="dot-fill" aria-hidden="true" />
            Primary reward
          </span>
          <span>
            <i className="dot-line" aria-hidden="true" />
            Secondary reward
          </span>
        </div>
      </div>
    </div>
  );
}

export default function PublicInfo() {
  const [tab, setTab] = useState("fsp");
  const items = publicInfoItems();
  const snap = radrootConfig.providerSnapshot;
  const info = radrootConfig.publicInfo;
  if (!hasPublicInfo()) return null;

  return (
    <section className="section" id="public-info" data-testid="public-info-section">
      <div className="wrap">
        <SectionHead
          index="04"
          label="Radroot FTSO Provider Information"
          title="Provider status."
          right={snap ? <span className="infra-count">Coston2 testnet</span> : null}
        />

        {snap && (
          <Reveal>
            <div className="prov-panel" data-testid="provider-panel">
              <div className="prov-head">
                <div>
                  <div className="prov-entity" data-testid="provider-entity-name">
                    {snap.entityName}
                  </div>
                  <div className="prov-sub">
                    {snap.entityAddressShort}
                    <CopyButton value={snap.entityAddress} tid="copy-provider-address-btn" />
                  </div>
                </div>
                {info.explorerLink && (
                  <a className="explorer-link" href={info.explorerLink} target="_blank" rel="noopener noreferrer" data-testid="explorer-link">
                    View live on explorer
                    <ArrowUpRight size={14} />
                  </a>
                )}
              </div>

              <div className="prov-tabs" role="tablist" aria-label="Provider views">
                <button type="button" role="tab" aria-selected={tab === "fsp"} className={tab === "fsp" ? "on" : ""} data-testid="tab-fsp" onClick={() => setTab("fsp")}>
                  FSP
                </button>
                <button type="button" role="tab" aria-selected={tab === "ftso"} className={tab === "ftso" ? "on" : ""} data-testid="tab-ftso" onClick={() => setTab("ftso")}>
                  FTSO
                </button>
              </div>

              {tab === "fsp" ? <FspView data={snap.fsp} /> : <FtsoView data={snap.ftso} />}

              <div className="prov-note" data-testid="provider-snapshot-note">
                Snapshot from the Flare Coston2 systems explorer · {snap.snapshotDate} ·{" "}
                {info.explorerLink && (
                  <a href={info.explorerLink} target="_blank" rel="noopener noreferrer">
                    live values on the explorer
                  </a>
                )}
              </div>
            </div>
          </Reveal>
        )}

        {items.length > 0 && (
          <div className="pub-grid">
            {items.map((item, i) => (
              <Reveal key={item.key} delay={i * 0.06}>
                <div className="pub-item" data-testid={`public-info-${item.key}`}>
                  <div className="pub-label">{item.label}</div>
                  <Item item={item} />
                </div>
              </Reveal>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
